import { application } from '../../../../_runtime/bootstrap';
import { clone } from 'ramda';
import { v4 as uuidv4 } from 'uuid';

/**
 * Function: mapLayer
 *
 * This function maps an array of items, adding an order and elementId to each item.
 *
 * @param {any[]} arr - The array of items to map.
 * @param {string} guid - The element ID to assign to each item.
 * @returns {any[]} An array of items with order and elementId properties added.
 */
const mapLayer = (arr: any[], guid: string) =>
  arr.map((item: any, index: number) => {
    return { ...item, order: index, elementId: guid };
  });

/**
 * Function: mapPayloadToBackend
 *
 * This function maps form data to a format suitable for backend submission. It generates a new GUID if needed,
 * maps layers with order and elementId, and removes unnecessary properties.
 *
 * @param {boolean} isEditForm - Flag indicating if the form is for editing.
 * @param {any} data - The form data to map.
 * @param {boolean} isWallsTypes - Flag indicating if the data is for wall types.
 * @returns {any} The mapped data.
 */
export const mapPayloadToBackend = (
  isEditForm: boolean,
  data: any,
  isWallsTypes: boolean,
  overridenParameters: any[]
) => {
  const id = isEditForm ? data.id : uuidv4();
  const values = clone(data);

  values.id = id;
  values.layers.exterior = mapLayer(values.layers.exterior, id);
  values.layers.interior = mapLayer(values.layers.interior, id);
  values.layers.core = mapLayer(values.layers.core, id);

  values.layers = [
    ...values.layers.exterior,
    ...values.layers.interior,
    ...values.layers.core,
  ];

  const typeParameters = handleTypeParameters(
    values.id,
    values,
    overridenParameters
  );

  delete values['typeParameters'];
  if (!isWallsTypes) {
    delete values['side'];
  }

  return { values: values, typeParameters: typeParameters };
};

/**
 * Function: handleTypeParameters
 * 
 * This function processes type parameters by categorizing them into new, edited, and removed parameters.
 * It compares the current type parameters with the overridden parameters and determines the appropriate
 * actions to take for each parameter.
 * 
 * @param {string} id - The ID of the element to which the parameters belong.
 * @param {Object} data - The data containing the type parameters.
 * @param {Array} overridenParameters - The existing overridden parameters.
 * 
 * @returns {Object} An object containing arrays of new, edited, and removed parameters.
 */
export const handleTypeParameters = (
  id: string,
  data: any,
  overridenParameters: any[]
) => {
  const existentTypeParametersIds = overridenParameters.map(
    (param: any) => param.parameterId
  );
  const { typeParameters }: any = data;
  const allOverridenParameters = typeParameters
    .filter((param: any) => param.value !== undefined)
    .map((param: any) => {
      const typeParameter = overridenParameters.find(
        (overParam: any) => overParam.parameterId === param.id && overParam.elementId === id);
      return {
        id: typeParameter ? typeParameter.id : undefined,
        elementId: id,
        parameterId: param.id,
        name: param.name,
        description: '',
        value: param.value,
      };
    });

  const newTypeParameters = allOverridenParameters.filter(
    (param: any) => !existentTypeParametersIds.includes(param.parameterId)
  );

  const editedParameters = allOverridenParameters.filter((param: any) => {
    const existentParam = overridenParameters.find(
      (exParam: any) => exParam.parameterId === param.parameterId
    );
    return existentParam && existentParam.value !== param.value;
  });

  const removedTypeParameters = overridenParameters.filter(
    (overParam: any) =>
      !allOverridenParameters.some((param: any) => param.parameterId === overParam.parameterId)
  ).map((overParam: any) => overParam.id);

  return {
    newParameters: newTypeParameters,
    editedParameters: editedParameters,
    removedParameters: removedTypeParameters,
  };
};

/**
 * Function: getElementCategory
 *
 * This function determines the category of an element based on the pathname.
 *
 * @param {string} pathname - The current pathname.
 * @returns {string} The element category.
 */
export const getElementCategory = (pathname: string) => {
  if (pathname.includes('walls')) {
    return 'WALLS';
  }
  if (pathname.includes('roofs')) {
    return 'ROOFS';
  }
  if (pathname.includes('floors')) {
    return 'FLOORS';
  }
  return 'UNKNOWN';
};

/**
 * Function: handleTypeParametersRequests
 * 
 * This function handles requests for type parameters, including creating new parameters,
 * editing existing parameters, and removing parameters. It processes the parameters concurrently
 * and awaits all requests before resolving.
 * 
 * @param {Object} typeParameters - An object containing arrays of new, edited, and removed parameters.
 * @param {Array} typeParameters.newParameters - Array of new parameters to be created.
 * @param {Array} typeParameters.editedParameters - Array of parameters to be edited.
 * @param {Array} typeParameters.removedParameters - Array of parameter IDs to be removed.
 * 
 * @returns {Promise<void>} A promise that resolves when all parameter requests have been processed.
 */
export const handleTypeParametersRequests = async (typeParameters: any) => {
  const { newParameters, editedParameters, removedParameters } = typeParameters;
  const results = await Promise.all([
    Promise.all(newParameters.map(handleNewParameter)),
    Promise.all(editedParameters.map(handleEditedParameter)),
    Promise.all(removedParameters.map(handleRemovedParameter)),
  ]);
};

/**
 * Function: handleNewParameter
 * 
 * This function handles the creation of a new parameter by sending a POST request to the server.
 * 
 * @param {Object} param - The new parameter to be created.
 * 
 * @returns {Promise<void>} A promise that resolves when the parameter has been created.
 */
const handleNewParameter = async (param: any) => {
  application.getHttpClient().post(`/buildingms/types/parameters`, param);
};

/**
 * Function: handleEditedParameter
 * 
 * This function handles the editing of an existing parameter by sending a PUT request to the server.
 * 
 * @param {Object} param - The parameter to be edited.
 * 
 * @returns {Promise<void>} A promise that resolves when the parameter has been edited.
 */
const handleEditedParameter = async (param: any) => {
  application
    .getHttpClient()
    .put(`/buildingms/categories/parameters/${param.id}`, param);
};

/**
 * Function: handleRemovedParameter
 * 
 * This function handles the removal of a parameter by sending a DELETE request to the server.
 * 
 * @param {string} paramId - The ID of the parameter to be removed.
 * 
 * @returns {Promise<void>} A promise that resolves when the parameter has been removed.
 */
const handleRemovedParameter = async (paramId: any) => {
  application.getHttpClient().delete(`/buildingms/categories/parameters/${paramId}`);
};
